import { IonCol, IonRow } from '@ionic/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { GenericMessages } from 'src/intl/GenericMessages';
import { ProfileMessages } from 'src/intl/ProfileMessages';
import { AppModal } from '../reusable-components/app-modal/AppModal';
import { Input } from '../reusable-components/Input';

export interface EditProfileState {
  familyName: string;
  givenName: string;
}

interface EditProfileModalProps {
  /**
   * Whether the modal is open.
   */
  isOpen: boolean;
  /**
   * Triggers the request to close the modal.
   */
  onClose: () => void;
  /**
   * Triggers the request to close the modal and submit the edit-profile.
   */
  onSave: (e: EditProfileState) => void;
  /**
   * The user data object.
   */
  userData: {
    familyName?: string;
    givenName?: string;
  };
}

export const EditProfileModal: React.FC<EditProfileModalProps> = ({ onClose, isOpen, onSave, userData }) => {
  const { formatMessage } = useIntl();

  const initialState = useMemo(() => {
    return { familyName: userData.familyName ?? '', givenName: userData.givenName ?? '' };
  }, [userData.familyName, userData.givenName]);
  const [updatedProfileName, setUpdatedProfileName] = useState<EditProfileState>(initialState);

  useEffect(() => {
    setUpdatedProfileName(initialState);
  }, [initialState, isOpen]);

  const saveButton = useMemo(() => {
    return {
      label: formatMessage(GenericMessages.saveButtonLabel),
      onClick: () => {
        onSave(updatedProfileName);
      },
      disabled: !updatedProfileName.familyName || !updatedProfileName.givenName,
    };
  }, [formatMessage, onSave, updatedProfileName]);

  const cancelButton = React.useMemo(() => {
    return {
      label: formatMessage(GenericMessages.cancelButtonLabel),
      onClick: onClose,
    };
  }, [formatMessage, onClose]);

  return (
    <AppModal
      isOpen={isOpen}
      title={formatMessage(ProfileMessages.editProfile)}
      onClose={onClose}
      primaryButton={saveButton}
      secondaryButton={cancelButton}
    >
      <IonRow>
        <IonCol className="ion-no-padding ion-margin-end">
          <Input
            label={formatMessage(ProfileMessages.firstName)}
            value={updatedProfileName.givenName}
            onChange={e => setUpdatedProfileName({ ...updatedProfileName, givenName: e.detail.value! })}
            id="edit-profile-first-name"
            errorText={!updatedProfileName.givenName ? formatMessage(GenericMessages.fieldRequiredLabel) : undefined}
            required
          />
        </IonCol>
        <IonCol className="ion-no-padding">
          <Input
            label={formatMessage(ProfileMessages.lastName)}
            value={updatedProfileName.familyName}
            onChange={e => setUpdatedProfileName({ ...updatedProfileName, familyName: e.detail.value! })}
            id="edit-profile-last-name"
            errorText={!updatedProfileName.familyName ? formatMessage(GenericMessages.fieldRequiredLabel) : undefined}
            required
          />
        </IonCol>
      </IonRow>
    </AppModal>
  );
};

export default EditProfileModal;
